import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../lib/auth";
import { Microphone, Clock, Warning, ArrowRight, Sparkle, Calculator, Lock } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const isPremium = user?.is_admin || (user?.stripe_plan === "premium" && user?.subscription_status === "active");
  const sectorMap = {
    costruzioni: "Costruzioni",
    impiantistica: "Impiantistica",
    entrambi: "Costruzioni e Impiantistica",
    // Legacy fallback labels (in case migration hasn't run yet for the current /me call)
    edile: "Costruzioni",
    elettrico: "Impiantistica",
    idraulico: "Impiantistica",
    architetto: "Costruzioni",
    ingegnere: "Costruzioni",
  };

  const trialDays = (() => {
    if (user?.is_admin) return null;
    if (user?.subscription_status !== "trial" || !user?.trial_ends_at) return null;
    const days = Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  })();

  return (
    <Layout>
      <div data-testid="dashboard-page">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Ciao {user?.name?.split(" ")[0] || "—"}</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-none mt-1">
            Pronto<span className="text-[#FFD600]">.</span>
          </h1>
          {user?.sector && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-[#1A1A1A] border border-[#333]">
              <Sparkle size={16} weight="fill" className="text-[#FFD600]" /> Settore: {sectorMap[user.sector]}
            </div>
          )}
        </div>

        {trialDays !== null && (
          <Link to="/abbonamento" data-testid="trial-banner"
            className="block bg-gradient-to-br from-[#FFD600]/15 to-[#FFD600]/5 border border-[#FFD600]/30 rounded-md p-4 mb-6 hover:border-[#FFD600] transition">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#FFD600] font-bold mb-1">Prova Gratuita</div>
                <div className="font-heading text-2xl font-black uppercase">{trialDays} giorni rimasti</div>
                <div className="text-sm text-[#A3A3A3] mt-1">Standard 8€ · Premium 15€ al mese · Disdici quando vuoi</div>
              </div>
              <ArrowRight size={28} weight="bold" className="text-[#FFD600]" />
            </div>
          </Link>
        )}

        {user?.subscription_status === "expired" && (
          <Link to="/abbonamento" data-testid="expired-banner"
            className="block bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-md p-4 mb-6">
            <div className="font-heading text-xl font-black uppercase text-[#FF3B30]">Abbonamento scaduto</div>
            <div className="text-sm text-[#A3A3A3] mt-1">Riattiva per continuare a usare l'app</div>
          </Link>
        )}

        <div className="space-y-4">
          <Link to="/preventivo/nuovo" data-testid="action-new-quote"
            className="block bg-[#FFD600] text-[#0A0A0A] rounded-md p-6 active:scale-[0.98] transition shadow-[0_0_20px_rgba(255,214,0,0.2)]">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#0A0A0A]/10 rounded-md flex items-center justify-center">
                <Microphone size={40} weight="fill" />
              </div>
              <div className="flex-1">
                <div className="font-heading text-3xl font-black uppercase tracking-tight leading-none">Nuovo Preventivo</div>
                <div className="text-sm font-bold uppercase tracking-wider mt-1 opacity-80">Vocale + Foto AI</div>
              </div>
              <ArrowRight size={32} weight="bold" />
            </div>
          </Link>

          <Link to="/timbratura" data-testid="action-timeclock"
            className="block bg-[#1A1A1A] border-2 border-[#333] rounded-md p-6 hover:border-[#FFD600] active:scale-[0.98] transition">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#FFD600]/10 rounded-md flex items-center justify-center">
                <Clock size={40} weight="fill" className="text-[#FFD600]" />
              </div>
              <div className="flex-1">
                <div className="font-heading text-3xl font-black uppercase tracking-tight leading-none">Timbra Ore</div>
                <div className="text-sm font-bold uppercase tracking-wider mt-1 text-[#A3A3A3]">Check-in / Check-out</div>
              </div>
              <ArrowRight size={32} weight="bold" className="text-[#A3A3A3]" />
            </div>
          </Link>

          <Link to="/personale" data-testid="action-expiries"
            className="block bg-[#1A1A1A] border-2 border-[#333] rounded-md p-6 hover:border-[#FF3B30] active:scale-[0.98] transition">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#FF3B30]/10 rounded-md flex items-center justify-center">
                <Warning size={40} weight="fill" className="text-[#FF3B30]" />
              </div>
              <div className="flex-1">
                <div className="font-heading text-3xl font-black uppercase tracking-tight leading-none">Personale</div>
                <div className="text-sm font-bold uppercase tracking-wider mt-1 text-[#A3A3A3]">Dipendenti · Attestati · Scadenze</div>
              </div>
              <ArrowRight size={32} weight="bold" className="text-[#A3A3A3]" />
            </div>
          </Link>

          <Link to="/computo" data-testid="action-computo"
            className={`block rounded-md p-6 active:scale-[0.98] transition border-2 ${isPremium ? "bg-[#1A1A1A] border-[#FFD600]/40 hover:border-[#FFD600]" : "bg-[#1A1A1A] border-[#333] hover:border-[#FFD600]/60"}`}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#FFD600]/10 rounded-md flex items-center justify-center relative">
                <Calculator size={40} weight="fill" className="text-[#FFD600]" />
                {!isPremium && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-[#0A0A0A] rounded-full flex items-center justify-center border border-[#FFD600]/40">
                    <Lock size={12} weight="fill" className="text-[#FFD600]" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="font-heading text-3xl font-black uppercase tracking-tight leading-none flex items-center gap-2">
                  Verifica Computo
                  {isPremium && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFD600] text-[#0A0A0A] font-bold">AI</span>}
                </div>
                <div className="text-sm font-bold uppercase tracking-wider mt-1 text-[#A3A3A3]">
                  {isPremium ? "Carica PDF · Verifica matematica & misure" : "🔒 Funzione Premium · 15€/mese"}
                </div>
              </div>
              <ArrowRight size={32} weight="bold" className="text-[#FFD600]" />
            </div>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
